import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  BookOpen, Upload, Trash2, RefreshCw, ToggleLeft, ToggleRight,
  FileText, Eye, X, Loader2, Search, Edit, Check, AlertTriangle,
  Database, Layers
} from 'lucide-react';

export default function KnowledgePage() {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [previewSheet, setPreviewSheet] = useState(null);
  const [editSheet, setEditSheet] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);
  const [productName, setProductName] = useState('');
  const [categoria, setCategoria] = useState('General');
  const [precioDesde, setPrecioDesde] = useState('');

  // Edit form state
  const [editForm, setEditForm] = useState({ product_name: '', categoria: '', precio_desde: '' });

  useEffect(() => { loadSheets(); }, []);

  const loadSheets = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/knowledge');
      setSheets(data);
    } catch (err) { toast.error('Error al cargar fichas'); }
    setLoading(false);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !productName) {
      toast.error('Selecciona un archivo y nombre de producto');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('product_name', productName);
      formData.append('categoria', categoria);
      formData.append('precio_desde', precioDesde);
      formData.append('activo', 'true');
      await api.upload('/api/knowledge/upload', formData);
      toast.success('Ficha subida correctamente');
      setShowUploadForm(false);
      setUploadFile(null);
      setProductName('');
      setCategoria('General');
      setPrecioDesde('');
      loadSheets();
    } catch (err) { toast.error('Error al subir ficha: ' + err.message); }
    setUploading(false);
  };

  const toggleSheet = async (id) => {
    try {
      const result = await api.put(`/api/knowledge/${id}/toggle`);
      setSheets(prev => prev.map(s => s.id === id ? { ...s, activo: result.activo } : s));
      toast.success(result.activo ? 'Ficha activada' : 'Ficha desactivada');
    } catch (err) { toast.error('Error al cambiar estado'); }
  };

  const deleteSheet = async (id) => {
    if (!window.confirm('Eliminar esta ficha? Se perdera el contenido y los embeddings.')) return;
    try {
      await api.del(`/api/knowledge/${id}`);
      setSheets(prev => prev.filter(s => s.id !== id));
      toast.success('Ficha eliminada');
    } catch (err) { toast.error('Error al eliminar'); }
  };

  const reindexSheet = async (id) => {
    try {
      await api.post(`/api/knowledge/${id}/reindex`);
      toast.success('Re-indexando ficha en Qdrant...');
      setTimeout(loadSheets, 3000);
    } catch (err) { toast.error('Error al re-indexar'); }
  };

  const openPreview = async (id) => {
    try {
      const data = await api.get(`/api/knowledge/${id}`);
      setPreviewSheet(data);
    } catch (err) { toast.error('Error al cargar preview'); }
  };

  const openEdit = (sheet) => {
    setEditSheet(sheet);
    setEditForm({
      product_name: sheet.product_name,
      categoria: sheet.categoria,
      precio_desde: sheet.precio_desde || '',
    });
  };

  const saveEdit = async () => {
    if (!editSheet) return;
    try {
      const updated = await api.put(`/api/knowledge/${editSheet.id}`, editForm);
      setSheets(prev => prev.map(s => s.id === editSheet.id ? { ...s, ...updated } : s));
      toast.success('Ficha actualizada');
      setEditSheet(null);
    } catch (err) { toast.error('Error al actualizar'); }
  };

  const filtered = sheets.filter(s =>
    s.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.categoria.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (s.filename || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = {
    total: sheets.length,
    active: sheets.filter(s => s.activo).length,
    indexed: sheets.filter(s => s.indexed).length,
    totalChunks: sheets.reduce((acc, s) => acc + (s.chunk_count || 0), 0),
  };

  const CATEGORIES = ['General', 'Seguros', 'Finanzas', 'Tecnologia', 'Salud', 'Educacion', 'Energia', 'Otro'];

  return (
    <div className="p-6 lg:p-8 max-w-6xl" data-testid="knowledge-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Base de Conocimiento
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            Gestiona las fichas de producto para el analisis IA
          </p>
        </div>
        <button onClick={() => setShowUploadForm(!showUploadForm)} data-testid="upload-sheet-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all hover:shadow-lg"
          style={{ backgroundColor: 'var(--accent-primary)' }}>
          <Upload size={16} /> Subir Ficha
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total fichas', value: stats.total, icon: FileText, color: 'var(--accent-primary)' },
          { label: 'Activas', value: stats.active, icon: Check, color: '#22c55e' },
          { label: 'Indexadas', value: stats.indexed, icon: Database, color: '#8b5cf6' },
          { label: 'Chunks totales', value: stats.totalChunks, icon: Layers, color: '#f59e0b' },
        ].map(stat => (
          <div key={stat.label} className="rounded-xl border p-4 flex items-center gap-3"
            style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: stat.color + '20' }}>
              <stat.icon size={16} style={{ color: stat.color }} />
            </div>
            <div>
              <p className="text-lg font-bold font-mono" style={{ color: 'var(--text-primary)' }}>{stat.value}</p>
              <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Upload Form */}
      {showUploadForm && (
        <div className="rounded-xl border p-6 mb-6" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }} data-testid="upload-form">
          <h3 className="font-heading font-semibold text-base mb-4" style={{ color: 'var(--text-primary)' }}>Nueva Ficha de Producto</h3>
          <form onSubmit={handleUpload} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Archivo (.md, .txt)</label>
              <input type="file" accept=".md,.txt" onChange={e => setUploadFile(e.target.files[0])} data-testid="file-input"
                className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none"
                style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Nombre del Producto</label>
              <input type="text" value={productName} onChange={e => setProductName(e.target.value)} data-testid="product-name-input" placeholder="Ej: Seguro de Vida Premium"
                className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none"
                style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Categoria</label>
              <select value={categoria} onChange={e => setCategoria(e.target.value)} data-testid="category-select"
                className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none"
                style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Precio Desde</label>
              <input type="text" value={precioDesde} onChange={e => setPrecioDesde(e.target.value)} data-testid="price-input" placeholder="Ej: 99/mes"
                className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none"
                style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>
            <div className="flex items-end">
              <button type="submit" disabled={uploading} data-testid="submit-upload-btn"
                className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold text-white"
                style={{ backgroundColor: 'var(--accent-primary)' }}>
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                {uploading ? 'Subiendo...' : 'Subir'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
        <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-testid="search-sheets" placeholder="Buscar fichas..."
          className="w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm outline-none"
          style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin" style={{ color: 'var(--accent-primary)' }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center" style={{ color: 'var(--text-muted)' }}>
          <BookOpen size={48} className="mb-4 opacity-30" />
          <p className="text-lg font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Sin fichas de producto</p>
          <p className="text-sm">Sube fichas en formato .md o .txt para potenciar las sugerencias IA</p>
        </div>
      ) : (
        <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}>
          <table className="w-full" data-testid="sheets-table">
            <thead>
              <tr style={{ backgroundColor: 'var(--surface-secondary)' }}>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Producto</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Categoria</th>
                <th className="text-center px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Chunks</th>
                <th className="text-center px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Qdrant</th>
                <th className="text-center px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Estado</th>
                <th className="text-right px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(sheet => (
                <tr key={sheet.id} className="border-t transition-colors" style={{ borderColor: 'var(--border-color)' }}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <FileText size={16} style={{ color: 'var(--accent-primary)' }} />
                      <div>
                        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{sheet.product_name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>{sheet.filename}</span>
                          {sheet.precio_desde && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--surface-secondary)', color: 'var(--text-muted)' }}>
                              {sheet.precio_desde}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-xs px-2 py-1 rounded-md" style={{ backgroundColor: 'var(--surface-secondary)', color: 'var(--text-secondary)' }}>
                      {sheet.categoria}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-center">
                    <span className="text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>{sheet.chunk_count || 0}</span>
                  </td>
                  <td className="px-5 py-3 text-center" data-testid={`index-status-${sheet.id}`}>
                    {sheet.indexed ? (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 font-medium">
                        <Check size={10} /> Indexado
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 font-medium">
                        <AlertTriangle size={10} /> Pendiente
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-center">
                    <button onClick={() => toggleSheet(sheet.id)} data-testid={`toggle-sheet-${sheet.id}`} className="inline-flex items-center gap-1">
                      {sheet.activo ? <ToggleRight size={22} className="text-green-500" /> : <ToggleLeft size={22} style={{ color: 'var(--text-muted)' }} />}
                    </button>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(sheet)} data-testid={`edit-sheet-${sheet.id}`} className="p-2 rounded-lg transition-colors hover:opacity-80" style={{ color: 'var(--text-muted)' }} title="Editar">
                        <Edit size={14} />
                      </button>
                      <button onClick={() => openPreview(sheet.id)} data-testid={`preview-sheet-${sheet.id}`} className="p-2 rounded-lg transition-colors hover:opacity-80" style={{ color: 'var(--text-muted)' }} title="Preview">
                        <Eye size={14} />
                      </button>
                      <button onClick={() => reindexSheet(sheet.id)} data-testid={`reindex-sheet-${sheet.id}`} className="p-2 rounded-lg transition-colors hover:opacity-80" style={{ color: 'var(--text-muted)' }} title="Re-indexar">
                        <RefreshCw size={14} />
                      </button>
                      <button onClick={() => deleteSheet(sheet.id)} data-testid={`delete-sheet-${sheet.id}`} className="p-2 rounded-lg transition-colors hover:bg-red-500/20 text-red-400" title="Eliminar">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Preview Modal */}
      {previewSheet && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setPreviewSheet(null)}>
          <div className="w-full max-w-2xl max-h-[80vh] rounded-xl border overflow-hidden"
            style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}
            onClick={e => e.stopPropagation()} data-testid="preview-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
              <div>
                <h3 className="font-heading font-semibold" style={{ color: 'var(--text-primary)' }}>{previewSheet.product_name}</h3>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{previewSheet.chunk_count} chunks | {previewSheet.categoria}</p>
              </div>
              <button onClick={() => setPreviewSheet(null)} data-testid="close-preview" className="p-1 rounded" style={{ color: 'var(--text-muted)' }}>
                <X size={18} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <pre className="text-sm whitespace-pre-wrap font-mono leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                {previewSheet.content || 'Sin contenido'}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editSheet && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setEditSheet(null)}>
          <div className="w-full max-w-md rounded-xl border p-6"
            style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}
            onClick={e => e.stopPropagation()} data-testid="edit-sheet-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-bold text-lg" style={{ color: 'var(--text-primary)' }}>Editar Ficha</h3>
              <button onClick={() => setEditSheet(null)} style={{ color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Nombre del Producto</label>
                <input type="text" value={editForm.product_name} onChange={e => setEditForm(p => ({ ...p, product_name: e.target.value }))} data-testid="edit-product-name"
                  className="w-full px-3 py-2 rounded-lg border text-sm outline-none"
                  style={{ backgroundColor: 'var(--surface-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Categoria</label>
                <select value={editForm.categoria} onChange={e => setEditForm(p => ({ ...p, categoria: e.target.value }))} data-testid="edit-category"
                  className="w-full px-3 py-2 rounded-lg border text-sm outline-none"
                  style={{ backgroundColor: 'var(--surface-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Precio Desde</label>
                <input type="text" value={editForm.precio_desde} onChange={e => setEditForm(p => ({ ...p, precio_desde: e.target.value }))} data-testid="edit-price"
                  className="w-full px-3 py-2 rounded-lg border text-sm outline-none"
                  style={{ backgroundColor: 'var(--surface-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditSheet(null)} className="px-4 py-2 rounded-lg text-sm font-medium" style={{ color: 'var(--text-muted)' }}>Cancelar</button>
              <button onClick={saveEdit} data-testid="save-edit-btn" className="px-5 py-2 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: 'var(--accent-primary)' }}>
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
